/**
 * API Configuration
 * Centralized configuration for all API endpoints and settings
 */

// Base Configuration
export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
export const API_VERSION = process.env.REACT_APP_API_VERSION || 'v1';
export const API_TIMEOUT = 30000; // 30 seconds

// Construct base API URL
export const getApiUrl = (path = '') => {
  const baseUrl = `${API_BASE_URL}/api/${API_VERSION}`;
  return path ? `${baseUrl}${path.startsWith('/') ? path : `/${path}`}` : baseUrl;
};

// API Endpoints Configuration
export const API_ENDPOINTS = {
  // Authentication
  AUTH: {
    LOGIN: '/auth/login',
    REGISTER: '/auth/register',
    LOGOUT: '/auth/logout',
    REFRESH: '/auth/refresh',
    FORGOT_PASSWORD: '/auth/forgot-password',
    RESET_PASSWORD: '/auth/reset-password',
    VERIFY_EMAIL: '/auth/verify-email',
  },

  // Admin Authentication
  ADMIN: {
    LOGIN: '/admin/auth/login',
    LOGOUT: '/admin/auth/logout',
    REFRESH: '/admin/auth/refresh',
    DASHBOARD: '/admin/dashboard',
    ORGANIZATIONS: '/admin/organizations',
    PLANS: '/admin/plans',
    VENDORS: '/admin/vendors',
    DEMO_REQUESTS: '/admin/demo-requests',
  },

  // Projects
  PROJECTS: {
    BASE: '/projects',
    BY_ID: (id) => `/projects/${id}`,
    MEMBERS: (id) => `/projects/${id}/members`,
    TASKS: (id) => `/projects/${id}/tasks`,
    STATISTICS: (id) => `/projects/${id}/statistics`,
    DOCUMENTS: (id) => `/projects/${id}/documents`,
    RISKS: (id) => `/projects/${id}/risks`,
    ISSUES: (id) => `/projects/${id}/issues`,
  },

  // Tasks
  TASKS: {
    BASE: '/tasks',
    BY_ID: (id) => `/tasks/${id}`,
    COMMENTS: (id) => `/tasks/${id}/comments`,
    DEPENDENCIES: (id) => `/tasks/${id}/dependencies`,
    ATTACHMENTS: (id) => `/tasks/${id}/attachments`,
  },

  // Users
  USERS: {
    BASE: '/users',
    BY_ID: (id) => `/users/${id}`,
    PROFILE: '/users/profile',
    SETTINGS: '/users/settings',
    SKILLS: '/users/skills',
  },

  // Dashboard
  DASHBOARD: {
    STATS: '/dashboard/stats',
    PROJECT_STATS: '/dashboard/project-stats',
    TASK_STATS: '/dashboard/task-stats',
    TIME_STATS: '/dashboard/time-stats',
    ACTIVITIES: '/dashboard/activities',
    EXECUTIVE: '/dashboard/executive',
    PROJECTS: '/dashboard/projects',
    TEAM: '/dashboard/team',
    FINANCIAL: '/dashboard/financial',
  },

  // Time Tracking
  TIME_ENTRIES: {
    BASE: '/time-entries',
    BY_ID: (id) => `/time-entries/${id}`,
    SUMMARY: '/time-entries/summary',
    EXPORT: '/time-entries/export',
  },

  // Milestones
  MILESTONES: {
    BASE: '/milestones',
    BY_ID: (id) => `/milestones/${id}`,
  },

  // Documents
  DOCUMENTS: {
    BASE: '/documents',
    BY_ID: (id) => `/documents/${id}`,
    FOLDERS: '/document-folders',
    UPLOAD: '/documents/upload',
    DOWNLOAD: (id) => `/documents/${id}/download`,
  },

  // Budgets
  BUDGETS: {
    BASE: '/budgets',
    BY_ID: (id) => `/budgets/${id}`,
  },

  // Expenses
  EXPENSES: {
    BASE: '/expenses',
    BY_ID: (id) => `/expenses/${id}`,
  },

  // Risks
  RISKS: {
    BASE: '/risks',
    BY_ID: (id) => `/risks/${id}`,
  },

  // Issues
  ISSUES: {
    BASE: '/issues',
    BY_ID: (id) => `/issues/${id}`,
  },

  // Resources
  RESOURCES: {
    ALLOCATIONS: '/resource-allocations',
    ALLOCATION_BY_ID: (id) => `/resource-allocations/${id}`,
    AVAILABILITY: '/resource-availability',
  },

  // Departments
  DEPARTMENTS: {
    BASE: '/departments',
    BY_ID: (id) => `/departments/${id}`,
  },

  // Vendors
  VENDORS: {
    BASE: '/vendors',
    BY_ID: (id) => `/vendors/${id}`,
    CONTACTS: (id) => `/vendors/${id}/contacts`,
    CONTRACTS: (id) => `/vendors/${id}/contracts`,
  },

  // Portfolios
  PORTFOLIOS: {
    BASE: '/portfolios',
    BY_ID: (id) => `/portfolios/${id}`,
  },

  // Reports
  REPORTS: {
    BASE: '/reports',
    BY_ID: (id) => `/reports/${id}`,
    GENERATE: '/reports/generate',
  },

  // Approvals
  APPROVALS: {
    BASE: '/approvals',
    BY_ID: (id) => `/approvals/${id}`,
    APPROVE: (id) => `/approvals/${id}/approve`,
    REJECT: (id) => `/approvals/${id}/reject`,
  },

  // Baselines
  BASELINES: {
    BASE: '/baselines',
    BY_ID: (id) => `/baselines/${id}`,
  },

  // Forecasts
  FORECASTS: {
    BASE: '/forecasts',
    BY_ID: (id) => `/forecasts/${id}`,
  },

  // Dependencies
  DEPENDENCIES: {
    BASE: '/dependencies',
    BY_ID: (id) => `/dependencies/${id}`,
  },

  // Stakeholders
  STAKEHOLDERS: {
    BASE: '/stakeholders',
    BY_ID: (id) => `/stakeholders/${id}`,
  },

  // Meetings
  MEETINGS: {
    BASE: '/meetings',
    BY_ID: (id) => `/meetings/${id}`,
    PARTICIPANTS: (id) => `/meetings/${id}/participants`,
  },

  // Quality Metrics
  QUALITY_METRICS: {
    BASE: '/quality-metrics',
    BY_ID: (id) => `/quality-metrics/${id}`,
  },

  // Integrations
  INTEGRATIONS: {
    BASE: '/integrations',
    BY_ID: (id) => `/integrations/${id}`,
  },

  // Templates
  TEMPLATES: {
    BASE: '/templates',
    BY_ID: (id) => `/templates/${id}`,
  },

  // Lessons Learned
  LESSONS_LEARNED: {
    BASE: '/lessons-learned',
    BY_ID: (id) => `/lessons-learned/${id}`,
  },

  // Notifications
  NOTIFICATIONS: {
    BASE: '/notifications',
    BY_ID: (id) => `/notifications/${id}`,
    MARK_READ: (id) => `/notifications/${id}/read`,
    MARK_ALL_READ: '/notifications/read-all',
  },

  // Activity Logs
  ACTIVITY_LOGS: {
    BASE: '/activity-logs',
    BY_ID: (id) => `/activity-logs/${id}`,
  },

  // Audit Logs
  AUDIT_LOGS: {
    BASE: '/audit-logs',
    BY_ID: (id) => `/audit-logs/${id}`,
  },

  // Settings
  SETTINGS: {
    BASE: '/settings',
    BY_KEY: (key) => `/settings/${key}`,
  },

  // Organizations
  ORGANIZATIONS: {
    BASE: '/organizations',
    BY_ID: (id) => `/organizations/${id}`,
  },

  // Subscriptions
  SUBSCRIPTIONS: {
    BASE: '/subscriptions',
    BY_ID: (id) => `/subscriptions/${id}`,
    PLANS: '/subscription-plans',
  },

  // Demo Requests (Public)
  DEMO_REQUESTS: {
    BASE: '/demo-requests',
    BY_ID: (id) => `/demo-requests/${id}`,
    AVAILABLE_SLOTS: '/demo-requests/available-slots',
    STATS: '/demo-requests/stats',
  },

  // Comments
  COMMENTS: {
    BASE: '/comments',
    BY_ID: (id) => `/comments/${id}`,
  },

  // Change Requests
  CHANGE_REQUESTS: {
    BASE: '/change-requests',
    BY_ID: (id) => `/change-requests/${id}`,
  },
};

// HTTP Methods
export const HTTP_METHODS = {
  GET: 'GET',
  POST: 'POST',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
};

// Request Headers
export const DEFAULT_HEADERS = {
  'Content-Type': 'application/json',
  'Accept': 'application/json',
};

// Storage Keys
export const STORAGE_KEYS = {
  TOKEN: 'token',
  ADMIN_TOKEN: 'adminToken',
  USER: 'user',
  ADMIN_USER: 'adminUser',
  REFRESH_TOKEN: 'refreshToken',
  THEME: 'theme',
  LANGUAGE: 'language',
};

// API Response Status Codes
export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  UNPROCESSABLE_ENTITY: 422,
  INTERNAL_SERVER_ERROR: 500,
  SERVICE_UNAVAILABLE: 503,
};

// eslint-disable-next-line import/no-anonymous-default-export
export default {
  API_BASE_URL,
  API_VERSION,
  API_TIMEOUT,
  API_ENDPOINTS,
  HTTP_METHODS,
  DEFAULT_HEADERS,
  STORAGE_KEYS,
  HTTP_STATUS,
  getApiUrl,
};


